import { NbMenuItem } from "@nebular/theme";

/** The Loans permission that shows the Technical manual entry (work item 882). */
export const TECHNICAL_MANUAL_PERMISSION = "View Technical Manual";

/** Marks the entry that opens the help panel for the current screen instead of navigating. */
export const CONTEXT_HELP_ACTION = "context-help";

/**
 * Backoffice side-navigation menu: Dashboard (home; the header logo goes here), Workflow (top
 * level - the workflow engine serves both Loans and Wallet, so it is not nested under either),
 * Loans (contracts), Wallet (management system, admin users), Admin (merchants, suppliers,
 * users, roles) and Help sections. An entry with a `permission` in its data is shown only to
 * holders of it; see {@link menuFor}.
 */
export const MENU_ITEMS: NbMenuItem[] = [
  {
    title: "Dashboard",
    icon: "pie-chart-outline",
    link: "/dashboard",
    home: true,
  },
  {
    title: "Workflow",
    icon: "email",
    link: "/workflow",
  },
  {
    // A group only. Its link was /pages/dashboard, which never existed.
    title: "Loans",
    icon: "credit-card",
    children: [
      {
        title: "Contracts",
        link: "/contracts",
        icon: "credit-card",
      },
    ],
  },
  {
    title: "Wallet",
    icon: "shopping-bag-outline",
    children: [
      {
        title: "Management System",
        icon: "settings-outline",
        link: "/management-system-users",
      },
      {
        title: "Wallet Admin Users",
        icon: "people",
        link: "/wallet-user-admin",
      },
      {
        // Work item 936. Everyone in the backoffice may look; only a named grant may replay.
        title: "Card Payments To Credit",
        icon: "alert-triangle-outline",
        link: "/wallet-card-payments",
      },
      {
        // Work item 938: support's "where did this card sale go?" screen.
        title: "Device Lookup",
        icon: "search-outline",
        link: "/wallet-device-lookup",
      },
      {
        // Its own group under Wallet (work item 818): the collections engine has several screens
        // and more coming (invoices, letters), so they do not sit level with the wallet admin items.
        title: "Wallet Collections",
        icon: "layers-outline",
        children: [
          {
            title: "Collection Plans",
            icon: "calendar-outline",
            link: "/collections/plans",
          },
          {
            title: "Collection Products",
            icon: "pricetags-outline",
            link: "/collections/products",
          },
          {
            title: "Collection Runs",
            icon: "play-circle-outline",
            link: "/collections/run",
          },
          {
            title: "Device Inventory",
            icon: "smartphone-outline",
            link: "/collections/inventory",
          },
        ],
      },
    ],
  },
  {
    title: "Admin",
    icon: "toggle-right",
    children: [
      {
        title: "Merchants",
        icon: "shopping-cart",
        link: "/merchants",
      },
      {
        title: "Suppliers",
        icon: "car",
        link: "/suppliers",
      },
      {
        title: "Municipalities",
        icon: "map",
        link: "/municipalities",
      },
      {
        title: "Users",
        icon: "people",
        link: "/users",
      },
      {
        title: "Roles",
        icon: "person",
        link: "/roles",
      },
    ]
  },
  {
    // Work items 830 and 882. The user manual opens in its own tab, with its search and contents;
    // "Help for this page" opens the side panel on the current screen's section instead.
    title: "Help",
    icon: "question-mark-circle-outline",
    children: [
      {
        title: "Help for this page",
        icon: "info-outline",
        data: { action: CONTEXT_HELP_ACTION },
      },
      {
        title: "User manual",
        icon: "book-open-outline",
        url: "/help/index.html",
        target: "_blank",
      },
      {
        // Work item 923: no permission, every backoffice user may see what changed.
        title: "Release history",
        icon: "clock-outline",
        link: "/release-history",
      },
      {
        title: "Technical manual",
        icon: "code-outline",
        link: "/technical-manual",
        data: { permission: TECHNICAL_MANUAL_PERMISSION },
      },
    ],
  },
];

/**
 * The menu as this user should see it: entries whose data names a permission the user lacks are
 * left out. Hiding an entry is a courtesy, not the control; the API refuses the content anyway.
 */
export function menuFor(permissions: readonly string[]): NbMenuItem[] {
  const visible = (items: NbMenuItem[]): NbMenuItem[] => items
    .filter((item) => !item.data?.permission || permissions.includes(item.data.permission))
    .map((item) => (item.children ? { ...item, children: visible(item.children) } : item));
  return visible(MENU_ITEMS);
}
